import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { setHours, setMinutes, setSeconds } from 'date-fns';

import { filesAPIService } from '~/api/files';
import BuilderLocationBlock from '~/components/builder/BuilderLocationBlock';
import Accordion from '~/components/ui/Accordion/Accordion';
import AsyncSwitch from '~/components/ui/AsyncSwitch';
import ImageUploader from '~/components/ui/ImageUploader/ImageUploader';
import ImageUploaderModal from '~/components/ui/ImageUploaderModal/ImageUploaderModal';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import { COUPON_QR_ID_PARAM_NAME, QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME } from '~/constants/qr-code';
import useCouponQrData from '~/hooks/useCouponQrData';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import useCouponTemplateStore from '~/store/useCouponTemplateStore';
import { COLORS } from '~/styles/colors';
import { UploadedFile } from '~/types/qrCode';
import { getFileFromLocalSource, isMaxLength } from '~/utils/common';
import { logger } from '~/utils/logger';

export default function CouponTemplateBasicInformation() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { control, watch } = useFormContext();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const styles = useStyles();
  const {
    banner,
    setBanner,
    barcode,
    setBarcode,
    resetBarcode,
    resetBanner,
    locationType,
    setLocationType,
    setShowCompleteUrl,
    createOrUpdateQRError,
    isManuallyLocationCompleteAddress,
    setIsManuallyLocationCompleteAddress,
  } = useCouponTemplateStore();

  const [isBannerUploaderModalOpen, setIsBannerUploaderModalOpen] = useState(false);
  const [isBarcodeUploaderModalOpen, setIsBarcodeUploaderModalOpen] = useState(false);
  const [isOpenValidUntilPicker, setIsOpenValidUntilPicker] = useState(false);

  const useBarcode = watch('basicInfo.coupon.useBarcode');
  const couponId = searchParams.get(COUPON_QR_ID_PARAM_NAME);
  const isEdit = searchParams.get(QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME);
  const bannerError =
    createOrUpdateQRError?.response?.data &&
    'non_field_errors' in createOrUpdateQRError.response.data &&
    createOrUpdateQRError.response.data.non_field_errors.find((error: string) => error.includes('Barcode'));

  const { data: couponQrData } = useCouponQrData({ couponId, enabled: !!isEdit });

  const handleSaveBanner = ({
    zoom,
    roundness,
    rotate,
    file,
  }: {
    rotate: number;
    zoom: number;
    roundness: number;
    file: UploadedFile | string;
  }) => {
    if (typeof file === 'string') {
      setBanner({ rotate, borderRadius: roundness, scale: zoom });
    } else {
      setBanner({
        image: file.previewLink,
        imageId: file.imageStorageId,
        rotate,
        borderRadius: roundness,
        scale: zoom,
        isImageIdSentToDB: false,
      });
    }

    setIsBannerUploaderModalOpen(false);
  };

  const handleUploadBarcode = ({
    zoom,
    roundness,
    rotate,
    file,
  }: {
    rotate: number;
    zoom: number;
    roundness: number;
    file: UploadedFile | string;
  }) => {
    if (typeof file === 'string') {
      setBarcode({ rotate, borderRadius: roundness, scale: zoom });
    } else {
      setBarcode({
        image: file.previewLink,
        imageId: file.imageStorageId,
        rotate,
        borderRadius: roundness,
        scale: zoom,
        isImageIdSentToDB: false,
      });
    }

    setIsBarcodeUploaderModalOpen(false);
  };

  useEffect(() => {
    if (!isEdit && banner.image && !banner.imageId && !banner.isImageIdSentToDB) {
      (async function () {
        try {
          const bannerImageFile = await getFileFromLocalSource(`${window.location.origin}${banner.image}`);
          const bannerImageIdData = await filesAPIService.uploadImage(bannerImageFile);

          setBanner({ imageId: bannerImageIdData.data });
        } catch (error) {
          logger.log(error);
        }
      })();
    }
  }, []);

  useEffect(() => {
    if (couponQrData) {
      if (couponQrData.barcode) {
        setBarcode({
          image: couponQrData.barcode.image,
          rotate: couponQrData.barcode.rotate,
          borderRadius: couponQrData.barcode.borderRadius,
          scale: couponQrData.barcode.scale,
        });
      }

      if (couponQrData.banner) {
        setBanner({
          image: couponQrData.banner.image,
          rotate: couponQrData.banner.rotate,
          borderRadius: couponQrData.banner.borderRadius,
          scale: couponQrData.banner.scale,
        });
      }
    }
  }, [couponQrData]);

  const offerCompanyPath = 'basicInfo.offer.company';
  const offerTitlePath = 'basicInfo.offer.title';
  const offerDescriptionPath = 'basicInfo.offer.description';
  const offerSalesBadgePath = 'basicInfo.offer.salesBadge';
  const offerCodeButtonPath = 'basicInfo.offer.codeButton';
  const couponUseBarcodePath = 'basicInfo.coupon.useBarcode';
  const couponCodePath = 'basicInfo.coupon.code';
  const couponValidUntilPath = 'basicInfo.coupon.validUntil';
  const couponTermsPath = 'basicInfo.coupon.terms';
  const couponButtonTextPath = 'basicInfo.coupon.buttonText';
  const couponUrlPath = 'basicInfo.coupon.url';

  return (
    <CreateQRContentBlockLayout type="basicInfo">
      <Accordion
        defaultExpanded
        variant="child"
        summary={t('builder:coupon.offer.title')}
        details={
          <>
            <Text variant="inputSmall">{t('template5:image')}</Text>
            <ImageUploader
              image={banner.image}
              onDeleteImage={resetBanner}
              withUploaderModal
              onOpenUploaderModal={() => setIsBannerUploaderModalOpen(true)}
            />
            {isBannerUploaderModalOpen && (
              <ImageUploaderModal
                open={isBannerUploaderModalOpen}
                setOpen={setIsBannerUploaderModalOpen}
                image={banner.image}
                onSave={handleSaveBanner}
                defaultRotate={banner.rotate}
                defaultZoom={banner.scale}
                defaultRoundness={banner.borderRadius}
              />
            )}
            <Controller
              control={control}
              name={offerCompanyPath}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                const maxLengthError = isMaxLength(value, 70); // Shouldn't be red

                return (
                  <TextInput
                    id={offerCompanyPath}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!error?.message}
                    helperText={error?.message || maxLengthError}
                    inputProps={{ maxLength: 70 }}
                    fullWidth
                    label={t('other:company')}
                    placeholder={t('builder:coupon.offer.companyPlaceholder')}
                    containerSx={{ marginBottom: '12px', marginTop: '12px' }}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name={offerTitlePath}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                const maxLengthError = isMaxLength(value, 70); // Shouldn't be red

                return (
                  <TextInput
                    id={offerTitlePath}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!error?.message}
                    helperText={error?.message || maxLengthError}
                    inputProps={{ maxLength: 70 }}
                    fullWidth
                    label={`${t('other:title')} *`}
                    placeholder={t('builder:coupon.offer.titlePlaceholder')}
                    containerSx={{ marginBottom: '12px' }}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name={offerDescriptionPath}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                const maxLengthError = isMaxLength(value, 200); // Shouldn't be red

                return (
                  <TextInput
                    id={offerDescriptionPath}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!error?.message}
                    helperText={error?.message || maxLengthError}
                    inputProps={{ maxLength: 200 }}
                    fullWidth
                    label={t('other:description')}
                    placeholder={t('builder:coupon.offer.titlePlaceholder')}
                    containerSx={{ marginBottom: '12px' }}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name={offerSalesBadgePath}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                const maxLengthError = isMaxLength(value, 50); // Shouldn't be red

                return (
                  <TextInput
                    id={offerSalesBadgePath}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!error?.message}
                    helperText={error?.message || maxLengthError}
                    inputProps={{ maxLength: 50 }}
                    fullWidth
                    label={t('builder:coupon.offer.salesBadge')}
                    placeholder={t('builder:coupon.offer.salesBadgePlaceholder')}
                    containerSx={{ marginBottom: '12px' }}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name={offerCodeButtonPath}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                const maxLengthError = isMaxLength(value, 50); // Shouldn't be red

                return (
                  <TextInput
                    id={offerCodeButtonPath}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!error?.message}
                    helperText={error?.message || maxLengthError}
                    inputProps={{ maxLength: 50 }}
                    fullWidth
                    label={t('builder:coupon.offer.codeButton')}
                    placeholder={t('builder:coupon.offer.codeButtonPlaceholder')}
                  />
                );
              }}
            />
          </>
        }
      />
      <Accordion
        defaultExpanded
        variant="child"
        summary={`${t('builder:coupon.coupon.title')} *`}
        hideBottomBorder
        details={
          <>
            <Controller
              control={control}
              name={couponUseBarcodePath}
              render={({ field: { value, onChange, onBlur } }) => {
                return (
                  <AsyncSwitch
                    id={couponUseBarcodePath}
                    checked={value}
                    onBlur={onBlur}
                    onChange={(event) => {
                      onChange(event);

                      if (!event.target.checked) {
                        resetBarcode();
                      }
                    }}
                    label={t('builder:coupon.coupon.useBarcode')}
                  />
                );
              }}
            />
            {useBarcode ? (
              <Box sx={styles.barcodeImageUploader}>
                <Text variant="inputSmall" sx={{ marginTop: '12px' }}>
                  {t('builder:coupon.coupon.barcodeTitle')}
                </Text>
                <ImageUploader
                  image={barcode.image}
                  onDeleteImage={resetBarcode}
                  withUploaderModal
                  onOpenUploaderModal={() => setIsBarcodeUploaderModalOpen(true)}
                />
                {isBarcodeUploaderModalOpen && (
                  <ImageUploaderModal
                    image={barcode.image}
                    open={isBarcodeUploaderModalOpen}
                    setOpen={setIsBarcodeUploaderModalOpen}
                    onSave={handleUploadBarcode}
                    defaultRotate={barcode.rotate}
                    defaultZoom={barcode.scale}
                    defaultRoundness={barcode.borderRadius}
                  />
                )}
                {bannerError && (
                  <Text variant="inputSmall" sx={{ color: COLORS.error }}>
                    {bannerError}
                  </Text>
                )}
              </Box>
            ) : (
              <Controller
                control={control}
                name={couponCodePath}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                  const maxLengthError = isMaxLength(value, 30); // Shouldn't be red

                  return (
                    <TextInput
                      id={couponCodePath}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!error?.message}
                      helperText={error?.message || maxLengthError}
                      inputProps={{ maxLength: 30 }}
                      fullWidth
                      label={`${t('builder:coupon.coupon.code')} *`}
                      placeholder={t('builder:coupon.coupon.codePlaceholder')}
                      containerSx={{ marginBottom: '12px', marginTop: '12px' }}
                    />
                  );
                }}
              />
            )}
            <Controller
              control={control}
              name={couponValidUntilPath}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                return (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      id={couponValidUntilPath}
                      open={isOpenValidUntilPicker}
                      onOpen={() => setIsOpenValidUntilPicker(true)}
                      onClose={() => setIsOpenValidUntilPicker(false)}
                      value={value}
                      onChange={(date) => {
                        onChange(setHours(setMinutes(setSeconds(date, 59), 59), 23));
                      }}
                      minDate={new Date()}
                      enableAccessibleFieldDOMStructure={false}
                      slots={{
                        textField: TextInput,
                        openPickerButton: () => null,
                      }}
                      slotProps={{
                        textField: {
                          value,
                          onChange: () => {},
                          onBlur,
                          onClick: () => setIsOpenValidUntilPicker(true),
                          error: !!error?.message,
                          helperText: error?.message,
                          fullWidth: true,
                          label: t('builder:coupon.coupon.validUntil'),
                        },
                      }}
                    />
                  </LocalizationProvider>
                );
              }}
            />
            <Controller
              control={control}
              name={couponTermsPath}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                const maxLengthError = isMaxLength(value, 2000); // Shouldn't be red

                return (
                  <TextInput
                    id={couponTermsPath}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!error?.message}
                    helperText={error?.message || maxLengthError}
                    inputProps={{ maxLength: 2000 }}
                    fullWidth
                    label={t('builder:coupon.coupon.terms')}
                    placeholder={t('builder:coupon.coupon.termsPlaceholder')}
                    containerSx={{ marginBottom: '12px', marginTop: '12px' }}
                  />
                );
              }}
            />
            <Box sx={isDesktop ? styles.couponInfoBottom : styles.couponInfoBottomMobile}>
              <Controller
                control={control}
                name={couponButtonTextPath}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                  const maxLengthError = isMaxLength(value, 50); // Shouldn't be red

                  return (
                    <TextInput
                      id={couponButtonTextPath}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!error?.message}
                      helperText={error?.message || maxLengthError}
                      inputProps={{ maxLength: 50 }}
                      fullWidth
                      label={`${t('other:btnTxt')} *`}
                      placeholder={t('common:writeButtonText')}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name={couponUrlPath}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                  return (
                    <TextInput
                      id={couponUrlPath}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!error?.message}
                      helperText={error?.message}
                      fullWidth
                      label={`${t('labels:url')} *`}
                      placeholder="https://example.com"
                    />
                  );
                }}
              />
            </Box>
            <Text variant="t16Medium" sx={{ marginTop: '12px', marginBottom: '12px' }}>
              {t('other:location')}
            </Text>
            <BuilderLocationBlock
              locationType={locationType}
              onChangeLocationType={setLocationType}
              fieldNames={{
                complete: {
                  searchAddress: 'basicInfo.coupon.location.complete.searchAddress',
                  street: 'basicInfo.coupon.location.complete.street',
                  state: 'basicInfo.coupon.location.complete.state',
                  city: 'basicInfo.coupon.location.complete.city',
                  country: 'basicInfo.coupon.location.complete.country',
                  number: 'basicInfo.coupon.location.complete.number',
                  postalCode: 'basicInfo.coupon.location.complete.postalCode',
                },
                url: 'basicInfo.coupon.location.url',
                coordinates: {
                  longitude: 'basicInfo.coupon.location.coordinates.longitude',
                  latitude: 'basicInfo.coupon.location.coordinates.latitude',
                },
              }}
              onDeleteAddress={() => setShowCompleteUrl(false)}
              onApplyNewAddress={() => setShowCompleteUrl(true)}
              isManuallyAddress={isManuallyLocationCompleteAddress}
              onChangeIsManuallyAddress={setIsManuallyLocationCompleteAddress}
            />
          </>
        }
      />
    </CreateQRContentBlockLayout>
  );
}

function useStyles() {
  return {
    couponInfoBottom: css`
      display: flex;
      gap: 16px;
    `,
    couponInfoBottomMobile: css`
      display: flex;
      gap: 12px;
      flex-direction: column;
    `,
    barcodeImageUploader: css`
      margin-bottom: 12px;
    `,
  };
}
